import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Layer, type MapLayerMouseEvent, Source } from 'react-map-gl';
import { Feature, Geometry } from 'geojson';
import type Waypoint from '@alltrails/waypoints/types/Waypoint';
import { getTrailWaypoints } from '@alltrails/waypoints/api/getWaypoints';
import { useGroupHover } from '@alltrails/core';
import WaypointPopup from '../WaypointPopup';
import useMap from '../../hooks/useMap';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { defaultSymbolLayerLayoutProps, getLayerBeforeId, userWaypointsId } from '../../utils/layers';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import userWaypointSvg, { svgSizeWithoutShadow } from '../../utils/svgDefinitions/userWaypointSvg';

const svgDefinitions: SvgDefinition[] = [userWaypointSvg];

// If we just used the normal Waypoint type for the feature properties, the nested objects (like location) get stringified making it more complex
// to parse those values back from mapbox. So, flatten any nested fields at the time of feature creation to simplify the rest of the code.
type FeatureProperties = Waypoint & {
  latitude: number;
  longitude: number;
};

type UserWaypointsProps = { trailId: number };

const UserWaypoints = ({ trailId }: UserWaypointsProps) => {
  const [waypoints, setWaypoints] = useState<Waypoint[]>();
  const map = useMap();

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);
  const sourceId = 'user-waypoints';

  useEffect(() => {
    const fetchWaypoints = async () => {
      getTrailWaypoints(trailId)
        .then(setWaypoints)
        .catch(() => {
          console.log('Error fetching waypoints');
        });
    };
    fetchWaypoints();
  }, [trailId]);

  const features = useMemo(() => {
    if (!waypoints?.length) {
      return [];
    }
    return waypoints.map(waypoint => {
      const { latitude, longitude } = waypoint.location;
      const feature: Feature<Geometry, FeatureProperties> = {
        geometry: { type: 'Point', coordinates: [longitude, latitude] },
        properties: { ...waypoint, latitude, longitude },
        type: 'Feature'
      };
      return feature;
    });
  }, [waypoints]);

  const [hoveredWaypoint, setHoveredWaypoint] = useState<FeatureProperties>();

  const onHover = useCallback((waypoint: FeatureProperties) => {
    setHoveredWaypoint(waypoint);
  }, []);

  const onHoverEnd = useCallback(() => {
    setHoveredWaypoint(undefined);
  }, []);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true);

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      if (onMouseEnter && event.features) {
        const waypoint = event.features[0].properties as FeatureProperties;
        onMouseEnter(waypoint);
      }
    },
    [onMouseEnter]
  );

  useEffect(() => {
    registerEventHandler(userWaypointsId, 'mousemove', onMouseMove);
    onMouseLeave && registerEventHandler(userWaypointsId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(userWaypointsId, 'mousemove', onMouseMove);
      onMouseLeave && unregisterEventHandler(userWaypointsId, 'mouseleave', onMouseLeave);
    };
  }, [onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  const popup = useMemo(() => {
    if (!hoveredWaypoint) {
      return null;
    }

    const { description, latitude, longitude, name } = hoveredWaypoint;
    return (
      <WaypointPopup
        anchorSize={svgSizeWithoutShadow}
        description={description}
        latitude={latitude}
        longitude={longitude}
        onMouseEnter={() => onMouseEnter?.(hoveredWaypoint)} // Just prevents the popup from closing when the mouse leaves the map layer
        onMouseLeave={onMouseLeave}
        title={name}
      />
    );
  }, [hoveredWaypoint, onMouseEnter, onMouseLeave]);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
        <Layer
          id={userWaypointsId}
          source={sourceId}
          beforeId={getLayerBeforeId(map, userWaypointsId)}
          type="symbol"
          layout={{ ...defaultSymbolLayerLayoutProps, 'icon-image': userWaypointSvg.name }}
        />
      </Source>
      {popup}
    </>
  );
};

export default UserWaypoints;
